import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelected } from '../PlanSelectionNewWhatIfs/PlanSelectionNewWhatIfsActions';
import { useProposedPlansQuery, ProposedPlanDTO } from './api/useProposedPlansQuery';
import { useSelectedPlan } from './hooks/useSelectedPlan';
import { Menu, MenuContent, MenuTrigger, MenuItem } from '../CommonComponents/OPL/Menu/Menu';
import { ListItem } from '../CommonComponents/OPL/List/ListItem';
import { Shimmer } from '../CommonComponents/OPL/Shimmer/Shimmer';
import { TruncatedText } from '../CommonComponents/OPL/Text/TruncatedText';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../CommonComponents/OPL/Box/Box';
import {
  PlanSelectionWrapper,
  SelectedPlanMenuButton,
  SelectedPlanMenuIcon,
} from './PlanSelection.styles';
import { PlanActionMenu } from './components/PlanActionMenu/PlanActionMenu';
import { useRouteParams } from './hooks/useRouteParams';
import { selectHasAccessEditPlans } from '../CommonSelectors/CommonAreaSelector';
import { useComparePlansDisplayOrderQuery } from '../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/api/useComparePlansDisplayOrderQuery';
import { PLANS_MIN_COUNT } from '../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/RetirementSideBySide.meta';
import {
  mapPlansByOrderedIds,
  orderPlanIdsByDisplayOrder,
} from '../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/utils/orderPlanIdsByDisplayOrder';

export const PlanSelection = () => {
  const { clientId } = useRouteParams();
  const { data, isLoading, isError, refetch } = useProposedPlansQuery();
  const { data: displayOrder } = useComparePlansDisplayOrderQuery({
    clientId,
    enabled: (data?.length ?? 0) >= PLANS_MIN_COUNT,
  });

  const orderedPlans = useMemo(() => {
    if (!data) {
      return null;
    }

    const planIds = data.map(plan => plan.planId);
    const orderedPlanIds = orderPlanIdsByDisplayOrder(planIds, displayOrder);

    return mapPlansByOrderedIds(data, orderedPlanIds);
  }, [data, displayOrder]);

  const defaultPlan = useSelectedPlan();

  const hasAccessEditPlans = useSelector(selectHasAccessEditPlans);

  const [selectedPlan, setSelectedPlan] = useState<ProposedPlanDTO | null>(null);

  const [menuButtonWidth, setMenuButtonWidth] = useState(0);

  const measuredButtonWidthRef = useCallback((node: HTMLButtonElement) => {
    if (node !== null) {
      setMenuButtonWidth(node.getBoundingClientRect().width);
    }
  }, []);

  useEffect(() => {
    setSelectedPlan(defaultPlan);
  }, [defaultPlan]);

  const dispatch = useDispatch();

  if (isLoading) {
    return (
      <PlanSelectionWrapper>
        <SelectedPlanMenuButton hasAccessEditPlans={hasAccessEditPlans}>
          <Shimmer width={85} height={16} as="span" />
          <SelectedPlanMenuIcon className="far fa-angle-down" />
        </SelectedPlanMenuButton>
      </PlanSelectionWrapper>
    );
  }

  if (isError) {
    return (
      <Box display="flex" flex="1" alignItems="center" justifyContent="center">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!orderedPlans || !selectedPlan) {
    return null;
  }

  return (
    <PlanSelectionWrapper>
      <Menu>
        <MenuTrigger>
          <SelectedPlanMenuButton
            ref={measuredButtonWidthRef}
            hasAccessEditPlans={hasAccessEditPlans}
          >
            <TruncatedText fontWeight={500} width={menuButtonWidth - 60} as="span" textAlign="left">
              {selectedPlan.name}
            </TruncatedText>
            <SelectedPlanMenuIcon className="far fa-angle-down" />
          </SelectedPlanMenuButton>
        </MenuTrigger>
        <MenuContent width={menuButtonWidth}>
          {orderedPlans.map(plan => (
            <MenuItem
              key={plan.planId}
              onSelect={() => {
                setSelectedPlan(plan);

                //  TODO (LEGACY): Remove after the migration is complete
                dispatch(setSelected(plan.planId, clientId));
              }}
            >
              <ListItem
                subText={plan.description ?? undefined}
                icon={plan.planId === selectedPlan.planId ? 'far fa-check' : undefined}
                variant={plan.planId === selectedPlan.planId ? 'selected' : undefined}
                ml={plan.planId === selectedPlan.planId ? 0 : 28}
              >
                {plan.name}
              </ListItem>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>

      {hasAccessEditPlans && <PlanActionMenu />}
    </PlanSelectionWrapper>
  );
};
